/**
 * Dual-Role Strings Registry
 * ===========================
 *
 * PURPOSE
 * -------
 * Some strings in this codebase serve two roles simultaneously:
 *   1. **Storage/internal value** — persisted in DB, XMP metadata, or used as state keys
 *   2. **User-visible display text** — rendered in the UI (directly or via a mapping)
 *
 * These are called "dual-role strings". When we localize the app, we MUST NOT translate
 * the storage values themselves (doing so would break filters, metadata, and data
 * compatibility). Instead, the UI layer maps each stable value to a translation key.
 *
 * DESIGN
 * ------
 * This file imports the ACTUAL source-of-truth const arrays / types from the codebase
 * and uses TypeScript `satisfies` to guarantee at compile time that:
 *   • Every stable value has a corresponding translation key (no gaps)
 *   • If a value is added/removed in the source type, this file fails to compile (no drift)
 *
 * For types defined as union literals (not runtime arrays), we define the mapping here
 * and bind it to the union type via `satisfies Record<UnionType, string>`.
 *
 * USAGE IN MIGRATION (Phase 1 tokenization)
 * ------------------------------------------
 * When tokenizing a component that displays a dual-role value:
 *
 *   import { colorTagKeys } from "@/i18n/dual-role-strings"
 *   // In JSX:
 *   {t(colorTagKeys[photo.colorTag])}
 *
 * The stable storage value (e.g. "Red") stays untouched in data; only the UI display
 * goes through i18next.
 *
 * VALIDATION
 * ----------
 * `npm run i18n:validate-dual-role` runs a script that:
 *   1. Imports this module (via tsx) to confirm it compiles cleanly
 *   2. Reads source definition files and verifies that the values declared here
 *      match the actual consts/types in the codebase (catches renames/additions)
 *   3. Reports any translation key that doesn't yet exist in locale JSON (warning)
 *
 * This script is non-blocking in CI during Phase 1 but should be promoted to
 * blocking once migration is complete and locale files are populated.
 */

import { ColorTagValues, CullingValues } from "@/utils/exif"
import { PhotoFlagValues, SortOptions } from "@/lib/catalog/types"
import { HistoryRecordAction } from "@/lib/catalog/types"
import type { ColorTag, CullingTag } from "@/utils/exif"
import type { PhotoFlagStatus, SortOption, DisplayGroup } from "@/lib/catalog/types"
import type { MaskType, PeopleMaskPart } from "@/core/types"
import type { ExportImageType } from "@/core/export-image"

// ─────────────────────────────────────────────────────────────────────────────
// Category: Color Tags
// Source: src/utils/exif.ts — ColorTagValues
// Storage: PhotoRecord.colorTag, XMP xmp.Label
// ─────────────────────────────────────────────────────────────────────────────

export const colorTagKeys = {
	Red: "photoTags.colorRed",
	Yellow: "photoTags.colorYellow",
	Green: "photoTags.colorGreen",
	Blue: "photoTags.colorBlue",
	Purple: "photoTags.colorPurple",
	"No Color": "photoTags.colorNone",
} as const satisfies Record<ColorTag, string>

// ─────────────────────────────────────────────────────────────────────────────
// Category: Culling Tags
// Source: src/utils/exif.ts — CullingValues
// Storage: PhotoRecord.manualCullingTag
// Note: "Auto" is a UI-only display state (null cullingTag), not in CullingValues
// ─────────────────────────────────────────────────────────────────────────────

export const cullingTagKeys = {
	Keeper: "cullingTagSetter.topPick",
	Candidate: "cullingTagSetter.candidate",
	Reject: "cullingTagSetter.reject",
} as const satisfies Record<CullingTag, string>

/** Separate key for the null/Auto display state (not a real CullingTag value) */
export const CULLING_AUTO_KEY = "cullingTagSetter.auto"

// ─────────────────────────────────────────────────────────────────────────────
// Category: Photo Flag Status
// Source: src/lib/catalog/types.ts — PhotoFlagValues
// Storage: PhotoRecord.flag
// ─────────────────────────────────────────────────────────────────────────────

export const photoFlagKeys = {
	flagged: "photoFlags.flagged",
	rejected: "photoFlags.rejected",
	unflagged: "photoFlags.unflagged",
} as const satisfies Record<PhotoFlagStatus, string>

// ─────────────────────────────────────────────────────────────────────────────
// Category: Sort Options
// Source: src/lib/catalog/types.ts — SortOptions
// Storage: ProjectInfoRecord.sortOption
// ─────────────────────────────────────────────────────────────────────────────

export const sortOptionKeys = {
	filenameAscending: "bottomToolBar.sortFilenameAscending",
	filenameDescending: "bottomToolBar.sortFilenameDescending",
	dateAscending: "bottomToolBar.sortDateAscending",
	dateDescending: "bottomToolBar.sortDateDescending",
	modifiedDateAscending: "bottomToolBar.sortModifiedDateAscending",
	modifiedDateDescending: "bottomToolBar.sortModifiedDateDescending",
	customReordered: "bottomToolBar.sortCustom",
} as const satisfies Record<SortOption, string>

// ─────────────────────────────────────────────────────────────────────────────
// Category: Display Groups
// Source: src/lib/catalog/types.ts — DisplayGroup type
// Storage: ProjectInfoRecord.displayGroup
// ─────────────────────────────────────────────────────────────────────────────

export const displayGroupKeys = {
	all: "bottomToolBar.displayGroupAll",
	references: "bottomToolBar.displayGroupReferences",
	locked: "bottomToolBar.displayGroupLocked",
	processed: "bottomToolBar.displayGroupProcessed",
	processing: "bottomToolBar.displayGroupProcessing",
	suggested: "bottomToolBar.displayGroupSuggested",
	unsupported: "bottomToolBar.displayGroupUnsupported",
	keepers: "bottomToolBar.displayGroupTopPicks",
	candidates: "bottomToolBar.displayGroupCandidates",
	rejects: "bottomToolBar.displayGroupRejects",
	cullingProcessing: "bottomToolBar.displayGroupCullingProcessing",
} as const satisfies Record<DisplayGroup, string>

// ─────────────────────────────────────────────────────────────────────────────
// Category: Mask Types
// Source: src/core/types.ts — MaskType type
// Storage: Mask.type in localAdjustments
// ─────────────────────────────────────────────────────────────────────────────

export const maskTypeKeys = {
	subject: "masks.typeSubject",
	radial: "masks.typeRadial",
	gradient: "masks.typeGradient",
	sky: "masks.typeSky",
	background: "masks.typeBackground",
	people: "masks.typePeople",
	brush: "masks.typeBrush",
} as const satisfies Record<MaskType, string>

export const peopleMaskPartLabelKeys = {
	entire: "masks.partPerson",
	facialSkin: "masks.partFacialSkin",
	bodySkin: "masks.partBodySkin",
	eyebrows: "masks.partEyebrows",
	eyes: "masks.partEyes",
	lips: "masks.partLips",
	clothing: "masks.partClothing",
	hair: "masks.partHair",
} as const satisfies Record<PeopleMaskPart, string>

/** Default English mask names as stored in mask-utils.ts create* functions */
export const DEFAULT_MASK_NAMES: Record<MaskType, string> = {
	subject: "Subject",
	radial: "Radial Gradient",
	gradient: "Linear Gradient",
	sky: "Sky",
	background: "Background",
	people: "People",
	brush: "Brush",
}

/** Reverse lookup: English mask name → MaskType */
const DEFAULT_MASK_NAME_TO_TYPE = new Map(
	Object.entries(DEFAULT_MASK_NAMES).map(([type, name]) => [name, type as MaskType]),
)

/** English people part labels for reverse lookup */
const PEOPLE_PART_ENGLISH: Record<string, PeopleMaskPart> = {
	Person: "entire",
	"Facial Skin": "facialSkin",
	"Body Skin": "bodySkin",
	Eyebrows: "eyebrows",
	Eyes: "eyes",
	Lips: "lips",
	Clothing: "clothing",
	Hair: "hair",
}

const MASK_GROUP_PATTERN = /^Mask (\d+)$/
const PERSON_PATTERN = /^Person (\d+)$/
const PERSON_PART_PATTERN = /^Person (\d+) (.+)$/

/**
 * Translate a stored English mask/group name for display.
 * Returns the i18n key + params, or null if the name is user-customized.
 */
export function getMaskDisplayName(
	name: string,
	maskType?: MaskType,
): { key: string; params?: Record<string, unknown> } | null {
	// 1. Exact mask type default name
	if (maskType && name === DEFAULT_MASK_NAMES[maskType]) {
		return { key: maskTypeKeys[maskType] }
	}

	// 2. Mask group pattern: "Mask 1", "Mask 2"
	const groupMatch = MASK_GROUP_PATTERN.exec(name)
	if (groupMatch) {
		return { key: "masks.groupName", params: { index: groupMatch[1] } }
	}

	// 3. Person with part: "Person 1 Facial Skin"
	const personPartMatch = PERSON_PART_PATTERN.exec(name)
	if (personPartMatch && personPartMatch[2]) {
		const partType = PEOPLE_PART_ENGLISH[personPartMatch[2]]
		if (partType) {
			return {
				key: "masks.personPartNumbered",
				params: { index: personPartMatch[1], partKey: peopleMaskPartLabelKeys[partType] },
			}
		}
	}

	// 4. Person only: "Person 1"
	const personMatch = PERSON_PATTERN.exec(name)
	if (personMatch) {
		return { key: "masks.personNumbered", params: { index: personMatch[1] } }
	}

	// 5. Bare "Person"
	if (name === "Person") {
		return { key: peopleMaskPartLabelKeys.entire }
	}

	// 6. Bare part name: "Facial Skin", "Eyes", etc.
	const partType = PEOPLE_PART_ENGLISH[name]
	if (partType) {
		return { key: peopleMaskPartLabelKeys[partType] }
	}

	// 7. Numbered mask type name: "Subject 1", "Sky 2", "Background 1"
	const numberedTypeMatch = /^(.+) (\d+)$/.exec(name)
	if (numberedTypeMatch && numberedTypeMatch[1]) {
		const typeFromNumbered = DEFAULT_MASK_NAME_TO_TYPE.get(numberedTypeMatch[1])
		if (typeFromNumbered) {
			return {
				key: "masks.typeNumbered",
				params: { typeKey: maskTypeKeys[typeFromNumbered], index: numberedTypeMatch[2] },
			}
		}
	}

	// 8. Mask type name without type context
	const typeFromName = DEFAULT_MASK_NAME_TO_TYPE.get(name)
	if (typeFromName) {
		return { key: maskTypeKeys[typeFromName] }
	}

	return null
}

// ─────────────────────────────────────────────────────────────────────────────
// Category: Export Image Types
// Source: src/core/export-image.ts — ExportImageType type
// Storage: ExportOptions.imageType, PublishOptions.imageType
// ─────────────────────────────────────────────────────────────────────────────

export const exportImageTypeKeys = {
	JPEG: "exportDialog.imageTypeJpeg",
	TIFF: "exportDialog.imageTypeTiff",
	PSD: "exportDialog.imageTypePsd",
} as const satisfies Record<ExportImageType, string>

// ─────────────────────────────────────────────────────────────────────────────
// Category: Crop Aspect Ratio (keyword values only; numeric ratios not localized)
// Source: src/components/Crop/AspectRatioMenu.tsx
// Storage: adjustment state cropRatio label
// ─────────────────────────────────────────────────────────────────────────────

export const cropAspectRatioKeys = {
	Custom: "crop.aspectRatioCustom",
	Original: "crop.aspectRatioOriginal",
} as const

// ─────────────────────────────────────────────────────────────────────────────
// Category: Default Preset Group
// Source: src/store/store-presets.ts — DEFAULT_PRESET_GROUP
// Note: User-created group names are user content and NOT localized.
// ─────────────────────────────────────────────────────────────────────────────

export const presetGroupKeys = {
	"User Presets": "presets.userPresets",
} as const

// ─────────────────────────────────────────────────────────────────────────────
// Category: Packaged (Built-in) Preset Categories & Names
// Source: assets/presets/ folder structure
// Storage: preset.group (folder name), preset.name (filename without extension)
// Note: Only packaged presets (readOnly=true) are localized.
//       User-imported preset names are user content and NOT localized.
// ─────────────────────────────────────────────────────────────────────────────

/** Packaged preset category (folder) names → i18n keys (used for ControlGroup headers) */
export const packagedPresetCategoryKeys: Record<string, string> = {
	Airy: "presets.categoryAiry",
	BW: "presets.categoryBW",
	Cream: "presets.categoryCream",
	Earth: "presets.categoryEarth",
	Moody: "presets.categoryMoody",
	Sage: "presets.categorySage",
} as const

/**
 * Packaged preset name prefix → i18n keys (used for individual preset display names).
 * Keys are the lowercase filename prefix as it appears in the stored preset name.
 * Values in locale JSON must be the exact string to display (no runtime case conversion).
 */
export const packagedPresetNamePrefixKeys: Record<string, string> = {
	airy: "presets.prefixAiry",
	bw: "presets.prefixBW",
	cream: "presets.prefixCream",
	earth: "presets.prefixEarth",
	moody: "presets.prefixMoody",
	sage: "presets.prefixSage",
} as const

/**
 * Resolve the display name for a packaged preset.
 *
 * Packaged preset names follow the pattern `{prefix}-{number}` (e.g. "airy-1").
 * We look up the prefix in `packagedPresetNamePrefixKeys` to get the translated
 * display prefix, then combine with the number via the template key.
 *
 * English output matches the original filename exactly (e.g. "airy-1").
 * Other locales produce their own equivalent (e.g. "aereo-1" in es-419).
 *
 * @param name   The stored preset name (filename without extension), e.g. "airy-1"
 * @param tFn    The i18next `t` function (components namespace expected)
 * @returns      The translated display name, or the original name as fallback
 */
export function getPackagedPresetDisplayName(
	name: string,
	tFn: (key: string, options?: Record<string, unknown>) => string,
): string {
	const match = name.match(/^(.+)-(\d+)$/)
	if (!match) return name

	const [, prefix, number] = match
	if (!prefix || !number) return name

	const prefixKey = packagedPresetNamePrefixKeys[prefix]
	if (!prefixKey) return name

	const translatedPrefix = tFn(prefixKey)
	return tFn("presets.presetNameWithNumber", {
		category: translatedPrefix,
		number,
	})
}

// ─────────────────────────────────────────────────────────────────────────────
// History Messages
//
// History records currently store pre-computed English text in `message: string`.
// The correct i18n pattern: store action code + params, render at display time.
//
//   Stored: { action: HistoryRecordAction.ApplyPreset, params: { presetName: "Moody" } }
//   Rendered: t(historyMessageKeys[record.action], record.params)
//
// Migration strategy: dual-read — if `message` is string → legacy render as-is;
// if messageKey is present → use t(). No existing data is broken.
// ─────────────────────────────────────────────────────────────────────────────

export const historyMessageKeys = {
	[HistoryRecordAction.ApplyPreset]: "historyPanel.applyPreset",
	[HistoryRecordAction.ApplyAIStyle]: "historyPanel.applyAiStyle",
	[HistoryRecordAction.AutoEnhance]: "historyPanel.autoLightingWb",
	[HistoryRecordAction.PasteAdjustments]: "historyPanel.pasteAdjustments",
	[HistoryRecordAction.ApplyColorMatch]: "historyPanel.applyColorMatch",
	[HistoryRecordAction.Reset]: "historyPanel.resetAll",
	[HistoryRecordAction.Import]: "historyPanel.original",
	[HistoryRecordAction.ManuallyAdjust]: "historyPanel.manualAdjust",
} as const satisfies Record<HistoryRecordAction, string>

/**
 * Sub-keys for ManuallyAdjust actions (the enum alone is too coarse).
 * When migrating, use `manualAdjustSubKeys[subAction]` with additional params.
 */
export const manualAdjustSubKeys = {
	maskAdd: "historyPanel.maskAdd",
	maskRemove: "historyPanel.maskRemove",
	healingReset: "historyPanel.healingResetRemovals",
	crop: "historyPanel.cropCrop",
	cropStraighten: "historyPanel.cropStraighten",
	cropReset: "historyPanel.cropReset",
	cropRotate: "historyPanel.cropRotate",
	cropFlip: "historyPanel.cropFlip",
	ratingUpdated: "historyPanel.ratingUpdated",
	ratingCleared: "historyPanel.ratingCleared",
	controlAdjustment: "historyPanel.adjustmentByName",
} as const

/**
 * Map from stored English history message → i18n key.
 * Covers all known static messages passed to saveCurrentAdjustmentState().
 */
export const historyControlNameKeys: Record<string, string> = {
	// Basic adjustments
	Temperature: "historyPanel.ctrlTemperature",
	Tint: "historyPanel.ctrlTint",
	Exposure: "historyPanel.ctrlExposure",
	Contrast: "historyPanel.ctrlContrast",
	Highlights: "historyPanel.ctrlHighlights",
	Shadows: "historyPanel.ctrlShadows",
	Whites: "historyPanel.ctrlWhites",
	Blacks: "historyPanel.ctrlBlacks",
	Texture: "historyPanel.ctrlTexture",
	Clarity: "historyPanel.ctrlClarity",
	Dehaze: "historyPanel.ctrlDehaze",
	Vibrance: "historyPanel.ctrlVibrance",
	Saturation: "historyPanel.ctrlSaturation",
	// Detail
	Sharpen: "historyPanel.ctrlSharpen",
	"Luminance Noise Reduction": "historyPanel.ctrlLuminanceNoiseReduction",
	"Color Noise Reduction": "historyPanel.ctrlColorNoiseReduction",
	// Vignette
	Vignette: "historyPanel.ctrlVignette",
	"Vignette Midpoint": "historyPanel.ctrlVignetteMidpoint",
	"Vignette Roundness": "historyPanel.ctrlVignetteRoundness",
	"Vignette Feather": "historyPanel.ctrlVignetteFeather",
	"Vignette Highlights": "historyPanel.ctrlVignetteHighlights",
	// Grain
	"Grain Amount": "historyPanel.ctrlGrainAmount",
	"Grain Size": "historyPanel.ctrlGrainSize",
	"Grain Roughness": "historyPanel.ctrlGrainRoughness",
	// Perspective / Transform
	Distortion: "historyPanel.ctrlDistortion",
	"Vertical Perspective": "historyPanel.ctrlVerticalPerspective",
	"Horizontal Perspective": "historyPanel.ctrlHorizontalPerspective",
	"Perspective Rotate": "historyPanel.ctrlPerspectiveRotate",
	"Perspective Aspect": "historyPanel.ctrlPerspectiveAspect",
	"Perspective Scale": "historyPanel.ctrlPerspectiveScale",
	"X Offset": "historyPanel.ctrlXOffset",
	"Y Offset": "historyPanel.ctrlYOffset",
	"Auto Vertical": "historyPanel.ctrlAutoVertical",
	// White Balance
	"Set As-Shot White Balance": "historyPanel.ctrlAsShotWB",
	"Set Custom White Balance": "historyPanel.ctrlCustomWB",
	"White Balance Picker": "historyPanel.ctrlWBPicker",
	// LUT / Color Profile
	"Set Color Profile": "historyPanel.ctrlSetColorProfile",
	"LUT Strength": "historyPanel.ctrlLutStrength",
	"Remove LUT": "historyPanel.ctrlRemoveLut",
	"Import LUT": "historyPanel.ctrlImportLut",
	// Lens Corrections
	"Correct Lens Distortion": "historyPanel.ctrlCorrectLensDistortion",
	"Correct Lens Vignette": "historyPanel.ctrlCorrectLensVignette",
	"Correct Lens Chromatic Aberration": "historyPanel.ctrlCorrectLensCA",
	"Enable Lens Corrections": "historyPanel.ctrlEnableLensCorrections",
	"Enable Profile Corrections": "historyPanel.ctrlEnableProfileCorrections",
	"Lens Distortion Correction Factor": "historyPanel.ctrlLensDistortionFactor",
	"Lens Vignette Correction Factor": "historyPanel.ctrlLensVignetteFactor",
	// Defringe
	DefringeGreen: "historyPanel.ctrlDefringeGreen",
	DefringePurple: "historyPanel.ctrlDefringePurple",
	DefringeGreenAmount: "historyPanel.ctrlDefringeGreenAmount",
	DefringePurpleAmount: "historyPanel.ctrlDefringePurpleAmount",
	DefringeGreenHueRange: "historyPanel.ctrlDefringeGreenHueRange",
	DefringePurpleHueRange: "historyPanel.ctrlDefringePurpleHueRange",
	// Curves
	Curves: "historyPanel.ctrlCurves",
	"Curves: Custom": "historyPanel.ctrlCurvesCustom",
	"Curves: Red Channel": "historyPanel.ctrlCurvesRed",
	"Curves: Green Channel": "historyPanel.ctrlCurvesGreen",
	"Curves: Blue Channel": "historyPanel.ctrlCurvesBlue",
	"Reset parametric curve": "historyPanel.ctrlResetParametricCurve",
	"Parametric Shadows": "historyPanel.ctrlParametricShadows",
	"Parametric Darks": "historyPanel.ctrlParametricDarks",
	"Parametric Lights": "historyPanel.ctrlParametricLights",
	"Parametric Highlights": "historyPanel.ctrlParametricHighlights",
	"Reset Parametric Shadows": "historyPanel.ctrlResetParametricShadows",
	"Reset Parametric Darks": "historyPanel.ctrlResetParametricDarks",
	"Reset Parametric Lights": "historyPanel.ctrlResetParametricLights",
	"Reset Parametric Highlights": "historyPanel.ctrlResetParametricHighlights",
	// Grading
	"Color Grading Blending": "historyPanel.ctrlGradingBlending",
	"Color Grading Balance": "historyPanel.ctrlGradingBalance",
	"Reset color grading": "historyPanel.ctrlResetGrading",
	"Shadows Hue": "historyPanel.ctrlShadowsHue",
	"Shadows Saturation": "historyPanel.ctrlShadowsSaturation",
	"Shadows Luminance": "historyPanel.ctrlShadowsLuminance",
	"Midtones Hue": "historyPanel.ctrlMidtonesHue",
	"Midtones Saturation": "historyPanel.ctrlMidtonesSaturation",
	"Midtones Luminance": "historyPanel.ctrlMidtonesLuminance",
	"Highlights Hue": "historyPanel.ctrlHighlightsHue",
	"Highlights Saturation": "historyPanel.ctrlHighlightsSaturation",
	"Highlights Luminance": "historyPanel.ctrlHighlightsLuminance",
	"Global Hue": "historyPanel.ctrlGlobalHue",
	"Global Saturation": "historyPanel.ctrlGlobalSaturation",
	"Global Luminance": "historyPanel.ctrlGlobalLuminance",
	// HSL Color Mixer
	"Red Hue": "historyPanel.ctrlRedHue",
	"Red Saturation": "historyPanel.ctrlRedSaturation",
	"Red Luminance": "historyPanel.ctrlRedLuminance",
	"Orange Hue": "historyPanel.ctrlOrangeHue",
	"Orange Saturation": "historyPanel.ctrlOrangeSaturation",
	"Orange Luminance": "historyPanel.ctrlOrangeLuminance",
	"Yellow Hue": "historyPanel.ctrlYellowHue",
	"Yellow Saturation": "historyPanel.ctrlYellowSaturation",
	"Yellow Luminance": "historyPanel.ctrlYellowLuminance",
	"Green Hue": "historyPanel.ctrlGreenHue",
	"Green Saturation": "historyPanel.ctrlGreenSaturation",
	"Green Luminance": "historyPanel.ctrlGreenLuminance",
	"Aqua Hue": "historyPanel.ctrlAquaHue",
	"Aqua Saturation": "historyPanel.ctrlAquaSaturation",
	"Aqua Luminance": "historyPanel.ctrlAquaLuminance",
	"Blue Hue": "historyPanel.ctrlBlueHue",
	"Blue Saturation": "historyPanel.ctrlBlueSaturation",
	"Blue Luminance": "historyPanel.ctrlBlueLuminance",
	"Purple Hue": "historyPanel.ctrlPurpleHue",
	"Purple Saturation": "historyPanel.ctrlPurpleSaturation",
	"Purple Luminance": "historyPanel.ctrlPurpleLuminance",
	"Magenta Hue": "historyPanel.ctrlMagentaHue",
	"Magenta Saturation": "historyPanel.ctrlMagentaSaturation",
	"Magenta Luminance": "historyPanel.ctrlMagentaLuminance",
	// Masks / Healing
	Remove: "historyPanel.ctrlRemove",
	"Reset Removals": "historyPanel.ctrlResetRemovals",
	"Remove Mask": "historyPanel.ctrlRemoveMask",
	"Rename Mask": "historyPanel.ctrlRenameMask",
	"Rename Mask Group": "historyPanel.ctrlRenameMaskGroup",
	"Update mask": "historyPanel.ctrlUpdateMask",
	"Add Mask": "historyPanel.ctrlAddMask",
	Sharpness: "historyPanel.ctrlSharpness",
	Feather: "historyPanel.ctrlFeather",
	Radius: "historyPanel.ctrlRadius",
	Flow: "historyPanel.ctrlFlow",
	Density: "historyPanel.ctrlDensity",
	Strength: "historyPanel.ctrlStrength",
	"AI Erase": "historyPanel.ctrlAiErase",
	"AI Generate": "historyPanel.ctrlAiGenerate",
	Retouching: "historyPanel.ctrlRetouching",
	// Point Color
	"Added Point Color Swatch": "historyPanel.ctrlAddPointColor",
	"Removed Point Color Swatch": "historyPanel.ctrlRemovePointColor",
	"Removed All Point Color Swatches": "historyPanel.ctrlRemoveAllPointColors",
	"Point Color Shifts": "historyPanel.ctrlPointColorShifts",
	"Saturation Shift": "historyPanel.ctrlSaturationShift",
	"Luminance Shift": "historyPanel.ctrlLuminanceShift",
	"Hue Shift": "historyPanel.ctrlHueShift",
	Range: "historyPanel.ctrlRange",
	"Hue Range": "historyPanel.ctrlHueRange",
	"Saturation Range": "historyPanel.ctrlSaturationRange",
	"Luminance Range": "historyPanel.ctrlLuminanceRange",
	// Calibration
	"Calibration Red Hue": "historyPanel.ctrlCalibrationRedHue",
	"Calibration Red Saturation": "historyPanel.ctrlCalibrationRedSat",
	"Calibration Green Hue": "historyPanel.ctrlCalibrationGreenHue",
	"Calibration Green Saturation": "historyPanel.ctrlCalibrationGreenSat",
	"Calibration Blue Hue": "historyPanel.ctrlCalibrationBlueHue",
	"Calibration Blue Saturation": "historyPanel.ctrlCalibrationBlueSat",
	// Crop
	Crop: "historyPanel.ctrlCrop",
	"Crop Straighten": "historyPanel.ctrlCropStraighten",
	"Reset Crop": "historyPanel.ctrlResetCrop",
	"Rotate CW": "historyPanel.ctrlRotateCW",
	"Rotate CCW": "historyPanel.ctrlRotateCCW",
	"Flip Horizontally": "historyPanel.ctrlFlipHorizontally",
	"Flip Vertically": "historyPanel.ctrlFlipVertically",
	"Auto Straighten": "historyPanel.ctrlAutoStraighten",
}

// ─────────────────────────────────────────────────────────────────────────────
// Category: Retouching Controls
// Source: src/components/People/PeoplePanel.tsx — retouchingSections config
// Storage: MaskGroup.retouching.control (slider titles), history messages (descriptions)
// ─────────────────────────────────────────────────────────────────────────────

export const retouchingSectionTitleKeys: Record<string, string> = {
	"Skin Retouching": "peoplePanel.skinRetouching",
	"Blemish Removal": "peoplePanel.blemishRemoval",
	Teeth: "peoplePanel.teeth",
	Eyes: "peoplePanel.eyes",
}

export const retouchingControlKeys: Record<string, string> = {
	Smoothing: "peoplePanel.smoothing",
	Enhancement: "peoplePanel.enhancement",
	Sculpt: "peoplePanel.sculpt",
	"Unify Tone": "peoplePanel.unifyTone",
	Acne: "peoplePanel.acne",
	Freckles: "peoplePanel.freckles",
	"Face Shine": "peoplePanel.faceShine",
	"Smile Line": "peoplePanel.smileLine",
	"Dark Circles": "peoplePanel.darkCircles",
	"Eye Bags": "peoplePanel.eyeBags",
	"Eye Wrinkles": "peoplePanel.eyeWrinkles",
	"Stray Hair": "peoplePanel.strayHair",
	"Flaw Removal": "peoplePanel.flawRemoval",
	Whitening: "peoplePanel.whitening",
	Alignment: "peoplePanel.alignment",
	Texture: "peoplePanel.texture",
	"Red Eye": "peoplePanel.redEye",
	"Glasses Glare": "peoplePanel.glassesGlare",
}

export const skinToneSwatchKeys: Record<string, string> = {
	"No skin tone": "peoplePanel.noSkinTone",
	Rose: "peoplePanel.rose",
	Mocha: "peoplePanel.mocha",
	Copper: "peoplePanel.copper",
	Blush: "peoplePanel.blush",
	Cool: "peoplePanel.cool",
	Peach: "peoplePanel.peach",
	White: "peoplePanel.white",
}

// ─────────────────────────────────────────────────────────────────────────────
// Aggregate exports (for lint exclusion lists, validation scripts)
// ─────────────────────────────────────────────────────────────────────────────

/** All registered stable values as a flat readonly array */
export const ALL_DUAL_ROLE_VALUES: readonly string[] = Object.freeze([
	...ColorTagValues,
	...CullingValues,
	...PhotoFlagValues,
	...SortOptions,
	...Object.keys(displayGroupKeys),
	...Object.keys(maskTypeKeys),
	...Object.keys(exportImageTypeKeys),
	...Object.keys(cropAspectRatioKeys),
	...Object.keys(presetGroupKeys),
	...Object.keys(retouchingControlKeys),
	...Object.keys(skinToneSwatchKeys),
])

/** Set version for O(1) lookups */
export const DUAL_ROLE_VALUES_SET: ReadonlySet<string> = new Set(ALL_DUAL_ROLE_VALUES)

/** Check if a string is a known dual-role storage value */
export const isDualRoleValue = (value: string): boolean => DUAL_ROLE_VALUES_SET.has(value)
